import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, BadgeCheck, HeartHandshake, Clock, CheckCircle2, AlertTriangle, Loader2, Crown,
} from "lucide-react";
import { money } from "@/lib/api";

// ---------- Trust labels ----------
const LABEL_META = {
  "Campaign Reviewed": { icon: ShieldCheck, cls: "bg-[#F3F4F6] text-ff-blue border-ff-blue" },
  "Organizer Verified": { icon: BadgeCheck, cls: "bg-[#F3F4F6] text-ff-blue border-ff-blue" },
  "Nonprofit Verified": { icon: HeartHandshake, cls: "bg-[#ECFDF3] text-ff-green border-ff-green" },
  "Payout Under Review": { icon: Clock, cls: "bg-[#FFF7ED] text-ff-amber border-ff-amber" },
  "Funds Released": { icon: CheckCircle2, cls: "bg-[#ECFDF3] text-ff-green border-ff-green" },
  "Reported / Under Review": { icon: AlertTriangle, cls: "bg-[#FEF2F2] text-ff-red border-ff-red" },
};

export function TrustChip({ label, size = "sm" }) {
  const meta = LABEL_META[label] || { icon: ShieldCheck, cls: "bg-[#F3F4F6] text-ff-dark border-ff-border" };
  const Icon = meta.icon;
  const pad = size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  return (
    <span
      data-testid={`trust-chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${pad} ${meta.cls}`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden="true" />
      {label}
    </span>
  );
}

export function TrustChips({ labels = [] }) {
  if (!labels.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {labels.map((l) => <TrustChip key={l} label={l} />)}
    </div>
  );
}

// ---------- Status pill ----------
const STATUS_META = {
  draft: "bg-[#F3F4F6] text-ff-dark",
  submitted: "bg-[#EFF6FF] text-[#1D4ED8]",
  under_review: "bg-[#FFF7ED] text-ff-amber",
  needs_info: "bg-[#FFF7ED] text-ff-amber",
  approved: "bg-[#ECFDF3] text-ff-green",
  live: "bg-[#ECFDF3] text-ff-green",
  paused: "bg-[#F3F4F6] text-ff-dark",
  rejected: "bg-[#FEF2F2] text-ff-red",
  completed: "bg-[#EFF6FF] text-[#1D4ED8]",
  archived: "bg-[#F3F4F6] text-ff-medium",
  requested: "bg-[#EFF6FF] text-[#1D4ED8]",
  held: "bg-[#FFF7ED] text-ff-amber",
  released: "bg-[#ECFDF3] text-ff-green",
  not_eligible: "bg-[#FEF2F2] text-ff-red",
  open: "bg-[#FEF2F2] text-ff-red",
  resolved: "bg-[#ECFDF3] text-ff-green",
  dismissed: "bg-[#F3F4F6] text-ff-medium",
  pending: "bg-[#FFF7ED] text-ff-amber",
};

export function StatusPill({ status }) {
  const cls = STATUS_META[status] || "bg-[#F3F4F6] text-ff-dark";
  return (
    <span data-testid={`status-pill-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${cls}`}>
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

// ---------- Layout helpers ----------
export function SectionLabel({ children }) {
  return (
    <div className="text-xs font-bold uppercase tracking-[0.2em] text-ff-medium">{children}</div>
  );
}

export function Container({ children, className = "" }) {
  return <div className={`mx-auto w-full max-w-7xl px-5 sm:px-8 ${className}`}>{children}</div>;
}

export function ProgressBar({ raised, goal }) {
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : Math.min(100, raised > 0 ? 60 : 0);
  return (
    <div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
        <div className="h-full rounded-full bg-ff-action transition-[width] duration-500"
             style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-heading text-lg font-extrabold text-ff-blue">{money(raised)}</span>
        <span className="text-sm text-ff-medium">
          {goal ? `raised of ${money(goal)}` : "raised · no fixed goal"}
        </span>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, action, testid = "empty-state" }) {
  return (
    <div data-testid={testid}
      className="flex flex-col items-center justify-center rounded-xl border border-dashed border-ff-border bg-white px-6 py-16 text-center">
      {Icon && <Icon className="mb-4 h-10 w-10 text-ff-medium" strokeWidth={1.6} />}
      <h3 className="font-heading text-xl font-bold text-ff-blue">{title}</h3>
      {message && <p className="mt-2 max-w-md text-sm text-ff-dark">{message}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function InlineLoader({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-ff-medium">
      <Loader2 className="h-6 w-6 animate-spin text-ff-red" />
      {label && <span className="text-sm">{label}…</span>}
    </div>
  );
}

export function CampaignCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm">
      <div className="aspect-[16/10] animate-pulse bg-[#E5E7EB]" />
      <div className="space-y-3 p-5">
        <div className="h-5 w-3/4 animate-pulse rounded bg-[#E5E7EB]" />
        <div className="h-3 w-full animate-pulse rounded bg-[#F3F4F6]" />
        <div className="h-2.5 w-full animate-pulse rounded-full bg-[#F3F4F6]" />
      </div>
    </div>
  );
}

export function CardGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="card-skeleton">
      {Array.from({ length: count }).map((_, i) => <CampaignCardSkeleton key={i} />)}
    </div>
  );
}

// ---------- Campaign card ----------
export function CampaignCard({ c }) {
  return (
    <Link
      to={`/fundraisers/${c.slug}`}
      data-testid={`campaign-card-${c.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#F3F4F6]">
        {c.cover_image ? (
          <img src={c.cover_image} alt={c.title}
               className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
        ) : null}
        <span className="absolute left-3 top-3 rounded-full bg-ff-blue/90 px-2.5 py-1 text-xs font-semibold text-white">
          {c.category}
        </span>
        {c.organizer_pro && (
          <span data-testid={`pro-badge-${c.slug}`} className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-ff-red px-2.5 py-1 text-xs font-bold text-white shadow-sm">
            <Crown className="h-3 w-3" /> PRO
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-lg font-bold leading-snug text-ff-blue line-clamp-2">{c.title}</h3>
        {c.organizer_verified_all && (
          <div data-testid={`verified-badge-${c.slug}`} title="Phone verified · Email verified · Payout account connected"
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-ff-green">
            <BadgeCheck className="h-3.5 w-3.5" /> Verified organizer
          </div>
        )}
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-ff-dark">{c.story}</p>
        <div className="mt-4">
          <ProgressBar raised={c.amount_raised} goal={c.goal_amount} />
        </div>
        {c.trust_labels?.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {c.trust_labels.slice(0, 2).map((l) => <TrustChip key={l} label={l} />)}
          </div>
        ) : null}
      </div>
    </Link>
  );
}
